'use client';

import {
  Button,
  Center,
  Heading,
  Inline,
  Input,
  Stack,
  Text,
} from '@cortex/ui';
import { Check } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { OPTION_BUTTON_BASE, QUESTION_ENTER_ANIMATION } from './styles';
import type { SingleSelectQuestionProps } from './types';

export function SingleSelectQuestion({
  acknowledgment,
  question,
  options,
  allowOther = false,
  onSelect,
}: SingleSelectQuestionProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [otherText, setOtherText] = useState('');
  const isOtherSelected = selectedId === '__other__';

  const handleOptionClick = (id: string) => {
    setSelectedId(id);
    if (id !== '__other__') {
      onSelect(id);
    }
  };

  const handleOtherSubmit = () => {
    if (otherText.trim()) {
      onSelect({ other: otherText.trim() });
    }
  };

  return (
    <Stack gap="lg" className={QUESTION_ENTER_ANIMATION}>
      {acknowledgment && (
        <Text color="muted" size="sm" className="italic">
          {acknowledgment}
        </Text>
      )}

      <Heading level={3} className="text-lg font-medium">
        {question}
      </Heading>

      <Stack gap="sm">
        {options.map((option) => (
          <Button
            key={option.id}
            type="button"
            variant="outline"
            onClick={() => handleOptionClick(option.id)}
            className={cn(
              OPTION_BUTTON_BASE,
              'hover:border-primary/50 hover:bg-accent/5',
              selectedId === option.id
                ? 'border-primary bg-primary/5'
                : 'border-border'
            )}
          >
            <Inline gap="sm" align="start">
              <Center
                className={cn(
                  'mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 transition-colors',
                  selectedId === option.id
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground/30'
                )}
              >
                {selectedId === option.id && (
                  <Check className="text-primary-foreground h-3 w-3" />
                )}
              </Center>
              <Stack gap="xs" className="flex-1">
                <Text as="span" weight="medium">
                  {option.label}
                </Text>
                {option.description && (
                  <Text color="muted" size="sm">
                    {option.description}
                  </Text>
                )}
              </Stack>
            </Inline>
          </Button>
        ))}

        {allowOther && (
          <Stack
            gap="none"
            className={cn(
              'rounded-lg border p-4 transition-all',
              isOtherSelected ? 'border-primary bg-primary/5' : 'border-border'
            )}
          >
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOptionClick('__other__')}
              className="w-full text-left"
            >
              <Inline gap="sm" align="start">
                <Center
                  className={cn(
                    'mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 transition-colors',
                    isOtherSelected
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/30'
                  )}
                >
                  {isOtherSelected && (
                    <Check className="text-primary-foreground h-3 w-3" />
                  )}
                </Center>
                <Text as="span" weight="medium">
                  Other (please specify)
                </Text>
              </Inline>
            </Button>

            {isOtherSelected && (
              <Inline
                gap="sm"
                className="animate-in fade-in slide-in-from-top-2 mt-3 duration-200"
              >
                <Input
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Please describe..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && otherText.trim()) {
                      handleOtherSubmit();
                    }
                  }}
                  autoFocus
                  className="flex-1"
                />
                <Button
                  onClick={handleOtherSubmit}
                  disabled={!otherText.trim()}
                  size="default"
                >
                  Continue
                </Button>
              </Inline>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
